import React, { useState } from 'react';
import classnames from 'classnames';
import { useIntl } from 'umi';
import { Bubble } from 'tea-component';
import SvgIcon from '@home/components/SvgIcon';
import StudioState from '@home/pages/studio/components/StudioState';
import CommonPDFPreviewDownload from '@intelligence/components/PDFPreviewDownload';
import useSearchParams from '@gp/hooks/useSearchParams';
import usePDFPreviewDownload from '@intelligence/pages/PDFPreviewDownload/_hooks/usePDFPreviewDownload';
import useLike from '@intelligence/pages/PDFPreviewDownloadLikeShare/_hooks/useLike';
import useShare from '@intelligence/pages/PDFPreviewDownloadLikeShare/_hooks/useShare';
import useView from '@intelligence/pages/PDFPreviewDownloadLikeShare/_hooks/useView';
import {
  pageReportTypeConfig,
  findReportObjectBySystemId,
  NEW_PDF_PREVIEW_STYLE_REPORTS,
} from '@intelligence/_config/reportConfig';
import { useChunkLoadErrorHandler } from '@intelligence/hooks/useChunkLoadErrorHandler';

import type { IBaseProps } from '@intelligence/types/common';

import '@intelligence/components/ReportCard/index.scss';
import '@intelligence/pages/PDFPreviewDownloadLikeShare/index.scss';

interface IPDFPreviewDownloadLikeShareProps extends IBaseProps { }

const PDFPreviewDownloadLikeShare: React.FC<IPDFPreviewDownloadLikeShareProps> = (props) => {
  const { } = props;

  useChunkLoadErrorHandler();

  const { formatMessage } = useIntl();

  const { referer } = useSearchParams();

  const {
    id,
    systemId,
    resourceId,
    resourceName,
    resourcePath,
    resourceOriginalPath,
    resourceLike,
    downloadable,
    downloadLogKey,
    watermarkEnable,
    fetchPDFUrl,
    lang,
    studioAble,
    studioId,
    studioAbleLoading,
    detailLoading,
    fileExtName,
  } = usePDFPreviewDownload();

  const reportObject = findReportObjectBySystemId(pageReportTypeConfig, systemId) || pageReportTypeConfig.gameAnalysis;

  // 是否无 Studio 权限
  const isStudioNoPermission =
    !studioAble && !studioAbleLoading && systemId === pageReportTypeConfig.studioReport.systemIdCur;

  // 是否是由新样式展示
  const isNewStyleReport = NEW_PDF_PREVIEW_STYLE_REPORTS.includes(systemId);

  const [likeBubble, setLikeBubble] = useState(false);
  const [shareBubble, setShareBubble] = useState(false);

  const { viewCnt } = useView({
    id,
    systemId,
    resourceId,
    resourceName,
    execute: reportObject?.showView,
    studioAble,
  });

  const { likeCnt, onLike } = useLike({
    id,
    systemId,
    resourceId,
    resourceName,
    resourceLike,
    setLikeBubble,
    setShareBubble,
    reportObject,
  });

  const { onShare } = useShare({
    id,
    systemId,
    resourceId,
    resourceName,
    resourcePath,
    resourceLike,
    setLikeBubble,
    setShareBubble,
    lang,
    reportObject,
    studioId,
  });

  const footer = (
    <div
      className={classnames({
        'report-loading-none': false,
        'dbi-reportcard-modal-footer': true,
        'dbi-pdf-preview-download-like-share-footer': true,
      })}
    >
      {/* 浏览量 */}
      {reportObject?.showView && (
        <div className="report-content-operate-item">
          <div className="report-content-operate-item-round report-view">
            <SvgIcon type="open-eyes" />
          </div>
          <div className="like-box">
            <div className="like-after">{formatMessage({ id: 'dbh_studio_report_view' })}</div>
            <span>{viewCnt}</span>
          </div>
        </div>
      )}
      {/* 点赞 */}
      {reportObject?.showLiked && (
        <div className="report-content-operate-item">
          <Bubble
            dark
            trigger="click"
            visible={likeBubble}
            content={formatMessage({ id: 'dbi_insight_report_feedback_tips' })}
          >
            <div
              className="report-content-operate-item-round report-like"
              onClick={() => {
                onLike();
              }}
            >
              <SvgIcon type="very-good" />
            </div>
            <div className="like-box">
              <div className="like-after">{formatMessage({ id: 'dbi_insights_report_like' })}</div>
              <span>{likeCnt}</span>
            </div>
          </Bubble>
        </div>
      )}
      {/* 分享 */}
      <div className="report-content-operate-item">
        <Bubble
          dark
          trigger="click"
          visible={shareBubble}
          content={formatMessage({ id: 'dbi_insight_report_share_success_tips1' })}
        >
          <div
            className="report-content-operate-item-round report-share"
            onClick={() => {
              onShare();
            }}
          >
            <SvgIcon type="aigc-share" />
          </div>
          <div>{formatMessage({ id: 'dbi_insights_report_share' })}</div>
        </Bubble>
      </div>
    </div>
  );

  const aside = (
    <div
      className={classnames({
        'dbi-report-card-modal-aside': true,
        'dbi-pdf-preview-download-like-share-aside': true,
      })}
    >
      {/* 点赞 */}
      {reportObject?.showLiked && (
        <div className="report-content-operate-item">
          <Bubble
            dark
            trigger="click"
            visible={likeBubble}
            content={formatMessage({ id: 'dbi_insight_report_feedback_tips' })}
          >
            <div
              className="report-content-operate-item-round report-like"
              onClick={() => {
                onLike();
              }}
            >
              <SvgIcon type="very-good" />
            </div>
          </Bubble>
        </div>
      )}
      {/* 分享 */}
      <div className="report-content-operate-item">
        <Bubble
          dark
          trigger="click"
          visible={shareBubble}
          content={formatMessage({ id: 'dbi_insight_report_share_success_tips1' })}
        >
          <div
            className="report-content-operate-item-round report-share"
            onClick={() => {
              onShare();
            }}
          >
            <SvgIcon type="aigc-share" />
          </div>
        </Bubble>
      </div>
    </div>
  );

  if (detailLoading) {
    return null;
  }

  // 如果报告是studio的,并且没有studio权限，才显示无权限的界面
  return (
    <>
      {isStudioNoPermission ? (
        <StudioState isNoPermission={true} />
      ) : (
        <CommonPDFPreviewDownload
          className="dbi-pdf-preview-download-like-share-page"
          referer={referer}
          fileName={resourceName}
          filePath={resourcePath}
          originalFilePath={resourceOriginalPath}
          downloadable={downloadable}
          downloadLogKey={downloadLogKey}
          watermarkEnable={watermarkEnable}
          systemId={systemId}
          studioId={studioId}
          resourceId={resourceId}
          fetchPDFUrl={fetchPDFUrl}
          reportObject={reportObject}
          lang={lang}
          fileExtName={fileExtName}
          aside={isNewStyleReport ? aside : undefined}
          footer={isNewStyleReport ? undefined : footer}
        />
      )}
    </>
  );
};

export default PDFPreviewDownloadLikeShare;
